import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { ChangePassword } from "../../components/ChangePassword";
import { toast } from "sonner";
import { getGeo, km } from "../operator/Dashboard";

export default function SubJobs() {
  const [data, setData] = useState(null);
  const [reasons, setReasons] = useState({});
  const [route, setRoute] = useState(null);
  const [snowOpen, setSnowOpen] = useState(false);
  const [avail, setAvail] = useState(null);
  const [showAvail, setShowAvail] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [exDate, setExDate] = useState("");
  const [exNote, setExNote] = useState("");
  const [exOff, setExOff] = useState(true);

  const loadAvail = () => api.get("/subcontractors/me/availability").then((r) => setAvail(r.data)).catch(() => {});

  const saveAvail = async (next) => {
    try { const r = await api.patch("/subcontractors/me/availability", next); setAvail(r.data); toast.success("Availability updated"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleDay = (d) => saveAvail({ weekly: { ...avail.weekly, [d]: !avail.weekly[d] } });
  const addException = () => {
    if (!exDate) return;
    saveAvail({ overrides: [...avail.overrides.filter((o) => o.date !== exDate), { date: exDate, available: !exOff, note: exNote.trim() }] });
    setExDate(""); setExNote("");
  };
  const dropException = (date) => saveAvail({ overrides: avail.overrides.filter((o) => o.date !== date) });

  const load = useCallback(() => api.get("/sub/jobs").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => {
    load();
    loadAvail();
    api.get("/snow/route").then((r) => setSnowOpen(!!r.data.event)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load/loadAvail are stable; polling interval by design
  }, [load]);

  const respond = async (oid, accept) => {
    try {
      await api.post(`/offers/${oid}/respond`, { accept, reason: reasons[oid] || "" });
      toast.success(accept ? "Job accepted" : "Declined");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return <Layout title="Jobs"><p className="text-muted-foreground">Loading…</p></Layout>;
  const active = data.jobs.filter((j) => ["assigned", "in_progress"].includes(j.status));
  const done = data.jobs.filter((j) => !["assigned", "in_progress"].includes(j.status));
  const activeSorted = route ? route.map((id) => active.find((j) => j.id === id)).filter(Boolean) : active;

  const suggestRoute = async () => {
    if (route) { setRoute(null); return; }
    const g = await getGeo();
    const pool = active.filter((j) => j.lat && j.lng);
    const noGeo = active.filter((j) => !j.lat || !j.lng);
    let cur = g || (pool[0] && { lat: pool[0].lat, lng: pool[0].lng });
    const ordered = [];
    const remaining = [...pool];
    while (remaining.length && cur) {
      remaining.sort((a, b) => km(cur.lat, cur.lng, a.lat, a.lng) - km(cur.lat, cur.lng, b.lat, b.lng));
      const next = remaining.shift();
      ordered.push(next);
      cur = { lat: next.lat, lng: next.lng };
    }
    setRoute([...ordered, ...noGeo].map((j) => j.id));
    if (pool.length) toast.success("Route order suggested — nearest stop first");
    else toast("No map locations on these jobs yet");
  };

  return (
    <Layout title="Jobs">
      {snowOpen && (
        <Link to="/snow-route" data-testid="sub-snow-route-banner" className="mb-4 flex h-14 items-center justify-center rounded-md bg-[hsl(196,72%,56%)] text-sm font-bold text-white">Snow event open — open the snow route</Link>
      )}
      {data.offers.length > 0 && (
        <section className="mb-8">
          <p className="label-caps mb-3">Offers for you</p>
          <div className="space-y-3" data-testid="offer-list">
            {data.offers.map((o) => (
              <div key={o.id} className="card-flat space-y-3 border-l-4 border-l-[hsl(196,72%,56%)] p-4" data-testid={`offer-card-${o.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-heading text-lg font-bold">{o.visit.property_name}</p>
                    <p className="text-sm text-muted-foreground">{o.visit.address}</p>
                  </div>
                  <span className="font-heading text-lg font-bold text-primary">{fmtMoney(o.visit.services.reduce((s, x) => s + x.rate, 0) || o.visit.sub_cost || "")}</span>
                </div>
                <p className="text-sm">{o.visit.services.map((s) => s.name).join(", ") || "Add-on work"} · {fmtDate(o.visit.date)}</p>
                {o.visit.instructions?.length > 0 && (
                  <div className="rounded-md bg-muted p-3 text-xs">
                    {o.visit.instructions.map((i) => <p key={i.id} className="font-semibold">+ {i.description}</p>)}
                  </div>
                )}
                {o.visit.access_notes && <p className="text-xs text-muted-foreground"><span className="font-bold">Access:</span> {o.visit.access_notes}</p>}
                <input data-testid={`decline-reason-${o.id}`} className="field-input w-full" placeholder="Reason (optional, for decline)"
                  value={reasons[o.id] || ""} onChange={(e) => setReasons({ ...reasons, [o.id]: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid={`accept-offer-${o.id}`} onClick={() => respond(o.id, true)} className="h-14 rounded-md bg-primary font-bold text-primary-foreground">Accept</button>
                  <button data-testid={`decline-offer-${o.id}`} onClick={() => respond(o.id, false)} className="h-14 rounded-md border border-destructive font-bold text-destructive">Decline</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mb-8" data-testid="availability-section">
        <button data-testid="availability-toggle" onClick={() => setShowAvail(!showAvail)}
          className="h-12 w-full rounded-md border border-border text-sm font-bold">My availability</button>
        {showAvail && avail && (
          <div className="card-flat mt-2 space-y-3 p-4" data-testid="availability-form">
            <p className="text-xs text-muted-foreground">Days you usually work. The office sees this when assigning jobs.</p>
            <div className="flex flex-wrap gap-2">
              {[["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]].map(([d, label]) => (
                <button key={d} type="button" data-testid={`avail-${d}`} onClick={() => toggleDay(d)}
                  className={`h-10 rounded-full border px-4 text-xs font-bold transition-colors ${avail.weekly[d] ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>{label}</button>
              ))}
            </div>
            <p className="label-caps pt-1">One-off exceptions</p>
            <div className="flex gap-2">
              <input data-testid="avail-ex-date" type="date" className="field-input flex-1" value={exDate} onChange={(e) => setExDate(e.target.value)} />
              <button type="button" data-testid="avail-ex-kind" onClick={() => setExOff(!exOff)}
                className="h-12 rounded-md border border-border px-3 text-xs font-bold">{exOff ? "Off" : "Working"}</button>
            </div>
            <div className="flex gap-2">
              <input data-testid="avail-ex-note" className="field-input flex-1" placeholder="Note (optional)" value={exNote} onChange={(e) => setExNote(e.target.value)} />
              <button data-testid="avail-ex-add" onClick={addException} disabled={!exDate} className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground disabled:opacity-40">Add</button>
            </div>
            {avail.overrides.length > 0 && (
              <div className="space-y-1">
                {avail.overrides.map((o) => (
                  <div key={o.date} className="flex items-center justify-between rounded-md border border-border p-2 text-xs" data-testid={`avail-override-${o.date}`}>
                    <span className="font-semibold">{fmtDate(o.date)} — {o.available ? "working (exception)" : "off"}{o.note ? ` · ${o.note}` : ""}</span>
                    <button data-testid={`avail-override-del-${o.date}`} onClick={() => dropException(o.date)} className="px-2 font-bold text-destructive">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <section className="mb-8" data-testid="account-section">
        <button data-testid="account-toggle" onClick={() => setShowAccount(!showAccount)}
          className="h-12 w-full rounded-md border border-border text-sm font-bold">My account</button>
        {showAccount && (
          <div className="card-flat mt-2 p-4">
            <ChangePassword />
          </div>
        )}
      </section>
      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <p className="label-caps" data-testid="active-jobs-heading">Active jobs</p>
          {active.length >= 2 && (
            <button data-testid="suggest-route-btn" onClick={suggestRoute} className="text-xs font-bold text-primary underline">
              {route ? "Clear route order" : "Suggest route order"}
            </button>
          )}
        </div>
        <div className="space-y-2" data-testid="active-jobs">
          {active.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No active jobs. Offers will appear above.</p>}
          {activeSorted.map((j) => (
            <Link key={j.id} to={`/sub/jobs/${j.id}`} data-testid={`job-row-${j.id}`}>
              <div className="card-flat flex items-center justify-between p-4 transition-colors hover:bg-muted/50">
                {route && <span data-testid={`route-order-${j.id}`} className="mr-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-primary-foreground">{route.indexOf(j.id) + 1}</span>}
                <div>
                  <p className="font-semibold">{j.property_name}</p>
                  {j.address && <p className="text-xs text-muted-foreground" data-testid={`job-address-${j.id}`}>{j.address}</p>}
                  <p className="text-sm text-muted-foreground">{fmtDate(j.date)} · {j.services.map((s) => s.name).join(", ") || "Add-on work"}</p>
                  {j.crew && <p className="mt-1 text-xs font-bold text-[hsl(196,72%,46%)]" data-testid={`team-line-${j.id}`}>Team job — with {(j.crew || []).filter((c) => c.id !== j.my_sub_id).map((c) => c.name).join(", ")}</p>}
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${j.status === "in_progress" ? "bg-[hsl(196,72%,56%)] text-[hsl(240,10%,10%)]" : "bg-secondary text-secondary-foreground"}`}>
                  {j.status === "in_progress" ? "On site" : "Assigned"}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {done.length > 0 && (
        <section>
          <p className="label-caps mb-3">Completed</p>
          <div className="space-y-2" data-testid="done-jobs">
            {done.slice(0, 10).map((j) => (
              <div key={j.id} className="card-flat flex items-center justify-between p-4">
                <div>
                  <p className="font-semibold">{j.property_name}</p>
                  <p className="text-sm text-muted-foreground">{fmtDate(j.date)}</p>
                </div>
                <span className="text-sm font-bold text-primary">{fmtMoney(j.my_cost ?? j.sub_cost)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
